import { useCallback } from 'react';
import http from 'hub-http/clients/apiClient';
import { fromCrmPropertiesToPartialCampaignProperties } from 'campaigns-lib/utils';
import { getPropertyValue, setPropertyValue } from 'marketing-platform-lib/utils/crmUtils';
import { registerMutation, registerQuery, useMutation, useQuery } from 'data-fetching-client';
import Raven from 'raven-js';
import { CAMPAIGN_PROPERTIES } from '../../shared/constants/campaignProperties';
import useAssetPreviewTracking from '../../shared/hooks/useAssetPreviewTracking';
import { partialUpdateCampaign } from 'campaigns-lib/data/CampaignDao';
const FETCH_UTMS_QUERY_FIELD_NAME = 'utms';
const DELTE_UTMS_QUERY_FIELD_NAME = 'delete_utms';
const FETCH_UTMS = registerQuery({
  fieldName: FETCH_UTMS_QUERY_FIELD_NAME,
  args: ['hsOriginAssetId'],
  fetcher({
    hsOriginAssetId
  }) {
    return http.get(`campaigns/v1/campaigns/${hsOriginAssetId}/utms`);
  }
});
const DELETE_UTMS = registerMutation({
  fieldName: DELTE_UTMS_QUERY_FIELD_NAME,
  args: ['campaignGuid', 'utm'],
  fetcher({
    campaignGuid,
    utm
  }) {
    return http.delete(`campaigns/v1/utms/${campaignGuid}/${utm}`);
  }
});
export function useFetchUTMs(crmObject) {
  const {
    trackAssetPreviewInteraction
  } = useAssetPreviewTracking();
  const hsOriginAssetId = crmObject ? getPropertyValue(crmObject, 'hs_origin_asset_id', '') : '';
  const {
    data,
    loading,
    error,
    refetch
  } = useQuery(FETCH_UTMS, {
    variables: {
      hsOriginAssetId
    },
    skip: !crmObject || hsOriginAssetId === '',
    onError: e => {
      Raven.captureException(e, {
        extra: {
          error: e
        },
        fingerprint: ['FETCH_UTMS_ERROR'],
        tags: {
          lib: 'framework-asset-preview-lib'
        }
      });
    },
    onCompleted: result => {
      var _result$utms;
      trackAssetPreviewInteraction({
        action: 'utm data loaded',
        maxCount: result === null || result === void 0 || (_result$utms = result.utms) === null || _result$utms === void 0 ? void 0 : _result$utms.length
      });
    }
  });
  const handleRefetch = useCallback(() => {
    refetch().catch(() => {});
  }, [refetch]);
  return {
    data: data === null || data === void 0 ? void 0 : data.utms,
    loading,
    error,
    refetch: handleRefetch
  };
}
export function useDeleteUTM({
  campaignGuid,
  utm,
  onCompleted,
  onError
}) {
  const [deleteUTM, {
    loading,
    error
  }] = useMutation(DELETE_UTMS, {
    variables: {
      campaignGuid,
      utm: encodeURI(utm)
    },
    // Update the cache to improve perceived performance
    update: cache => {
      cache.modify({
        id: 'ROOT_QUERY',
        fields: {
          [FETCH_UTMS_QUERY_FIELD_NAME]: utms => {
            return utms.filter(u => u.utm !== utm);
          }
        }
      });
    },
    onCompleted,
    onError: e => {
      Raven.captureException(e, {
        extra: {
          error
        },
        fingerprint: ['DELETE_UTM_ERROR'],
        tags: {
          lib: 'framework-asset-preview-lib'
        }
      });
      onError === null || onError === void 0 || onError(e);
    },
    refetchQueries: [FETCH_UTMS]
  });
  const handleDelete = useCallback(() => {
    // The error is handled in the useDeleteUTM onError callback
    deleteUTM().catch(() => {});
  }, [deleteUTM]);
  return {
    deleteUTM: handleDelete,
    loading,
    error
  };
}
export function updateCampaign({
  crmObject,
  changes,
  crmObjectProperties,
  isUngatedForEditableUTM
}) {
  const hsOriginAssetId = getPropertyValue(crmObject, 'hs_origin_asset_id', '');

  /**
   * Create a copy applying new values coming from @changes
   */
  let copyCrmObject = Object.assign({}, crmObject);
  Object.keys(changes).forEach(propertyName => {
    const changeValue = changes[propertyName];
    const mappedChangeValue = propertyName === CAMPAIGN_PROPERTIES.UTM && typeof changeValue === 'string' ? encodeURI(changeValue) : changeValue;
    copyCrmObject = setPropertyValue(copyCrmObject, propertyName, mappedChangeValue);
  });

  /**
   * The crmObject can contain properties that have been archived/deleted
   * If we included them in the payload, the api will return a validation error
   *
   * So we need to get a list of all currently valid properties and filter
   * properties from the @crmObject that are not in that list
   *
   * Note that we only apply this logic to customProperties, as
   * 1. They're the only ones that can be deleted
   * 2. Unlike hs properties, custom properties can be dropped from the payload
   * without being set to null by the backend
   */
  const validCustomPropertyNames = new Set((crmObjectProperties || []).filter(property => !property.hubspotDefined &&
  // Prevent attempts to update read only values
  // Best case scenario these would be ignored by the backend anyway
  // This should also filter out calculated properties
  // https://git.hubteam.com/HubSpot/framework-asset-preview-lib/pull/1676#discussion_r4568362
  // https://product.hubteam.com/docs/hubspot-framework/crm-objects/schemas/properties/property_definition_fields.html#readonlyvalue
  !property.readOnlyValue).map(property => property.name));

  /**
   * In the case where @crmObjectProperties is an empty array
   * (eg the network request was not skipped and is still loading):
   *
   * Archived/deleted properties being included in the crmObject is an
   * edge case, so rather than filtering all custom properties from the payload
   *   (potentially causing changes to be dropped)
   * or awaiting a properties fetch here
   *  (adding to the loading time of the request for a check that is usually unecessary)
   * we just let the payload go through as-is and allow it to fail
   * if necessary
   */
  const getIsValidCustomProperty = ({
    name
  }) => crmObjectProperties.length ? validCustomPropertyNames.has(name) : true;
  const propsForUTMUpdate = ['hs_name'];
  if (isUngatedForEditableUTM) {
    propsForUTMUpdate.push('hs_utm');
  }
  const propertiesToUpdate = Object.keys(changes).reduce((acc, key) => {
    acc[key] = copyCrmObject.properties[key];
    return acc;
  }, {});
  const partialCampaign = fromCrmPropertiesToPartialCampaignProperties({
    properties: propertiesToUpdate,
    options: {
      isValidCustomProperty: getIsValidCustomProperty
    }
  });
  return partialUpdateCampaign(partialCampaign, hsOriginAssetId, propsForUTMUpdate.some(key => key in changes) ? {
    preserveUtm: false
  } : undefined).then(campaignUpdateResponse => {
    if ('hs_name' in changes) {
      copyCrmObject = setPropertyValue(copyCrmObject, 'hs_utm', campaignUpdateResponse.utm);
      copyCrmObject.properties.hs_name.timestamp = Date.now();
    }
    if ('hs_ai_features' in copyCrmObject.properties && campaignUpdateResponse.aiFeatures) {
      copyCrmObject = setPropertyValue(copyCrmObject, 'hs_ai_features', JSON.stringify(campaignUpdateResponse.aiFeatures));
    }
    return copyCrmObject;
  });
}